import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Mail, Lock, User, Github, Chrome, DoorOpen, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { setUser, saveProfile, getProfile } from "@/lib/storage";
import { beginOAuth, claimCreatorReferral, enableFrontendWinterArcPreview, getDevAccounts, getDevModeStatus, getOnboardingProfile, login, loginAsDevAccount, loginAsDevGuest, setAccessToken, signup, STANDALONE_MODE } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { takeReturn } from "@/lib/returnTo";

const HERO_IMG = "https://images.unsplash.com/photo-1735948055457-8d816fb80a87";
export default function Auth() {
  const [mode, setMode] = useState("signup"); // 'signup' | 'signin'
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [devAccounts, setDevAccounts] = useState([]);
  const [guestLoginEnabled, setGuestLoginEnabled] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => { if (searchParams.get("mode") === "signin") setMode("signin"); }, [searchParams]);

  const persistSession = (session) => {
    setAccessToken(session.accessToken);
    setUser(session.user);
    if (!getProfile()) {
      saveProfile({ fullName: session.user?.fullName || "", completedAt: null });
    }
  };

  useEffect(() => {
    let cancelled = false;
    getDevModeStatus()
      .then((response) => {
        if (cancelled || !response.guestLoginEnabled) return;
        setGuestLoginEnabled(true);
        getDevAccounts()
          .then((accounts) => { if (!cancelled) setDevAccounts(accounts.accounts || []); })
          .catch(() => { /* Mock profiles may be disabled independently. */ });
      })
      .catch(() => { /* Production and unavailable APIs do not expose the shortcut. */ });
    return () => { cancelled = true; };
  }, []);

  const finishSession = async (session, message, description) => {
    persistSession(session);
    const referralCode = localStorage.getItem("hm_creator_referral_code");
    if (referralCode) await claimCreatorReferral(referralCode).catch(() => {});
    const profile = await getOnboardingProfile();
    const isComplete = Boolean(profile.completedAt || profile.isComplete);
    if (isComplete) saveProfile(profile);
    toast(message, { description });
    navigate(isComplete ? takeReturn("/dashboard") : "/onboarding");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || (mode === "signup" && !form.name)) {
      toast("Fill in the required fields to continue.");
      return;
    }
    setBusy(true);
    try {
      const session = mode === "signup"
        ? await signup({ name: form.name, email: form.email, password: form.password, referralCode: localStorage.getItem("hm_creator_referral_code") || undefined })
        : await login({ email: form.email, password: form.password });
      await finishSession(
        session,
        mode === "signup" ? "Account created." : "Welcome back.",
        mode === "signup" ? "Let's build your accountability profile." : "Continuing where you left off.",
      );
    } catch (error) {
      toast(mode === "signup" ? "Could not create account." : "Could not sign in.", {
        description: error.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const onDevLogin = async (account) => {
    setBusy(true);
    try {
      const session = await loginAsDevAccount(account.key);
      await finishSession(session, `Signed in as ${account.fullName}.`, `${account.subscriptionTier.toUpperCase()} development account`);
    } catch (error) {
      toast("Could not open the development account.", { description: error.message });
    } finally {
      setBusy(false);
    }
  };

  const onGuestLogin = async () => {
    setBusy(true);
    try {
      const session = await loginAsDevGuest();
      await finishSession(session, "Development guest opened.", "Login and onboarding were skipped.");
    } catch (error) {
      toast("Could not enter as guest.", { description: error.message });
    } finally {
      setBusy(false);
    }
  };

  const onWinterArcPreview = async () => {
    setBusy(true);
    try {
      enableFrontendWinterArcPreview();
      const session = await loginAsDevGuest();
      await finishSession(session, "Winter Arc preview opened.", "Frontend-only inspection mode — no backend writes.");
    } catch (error) {
      toast("Could not open the Winter Arc preview.", { description: error.message });
    } finally { setBusy(false); }
  };

  const onOAuth = async (provider) => {
    setBusy(true);
    try {
      const { authorizationUrl } = await beginOAuth(provider);
      window.location.assign(authorizationUrl);
    } catch (error) {
      toast(`${provider === "google" ? "Google" : "GitHub"} login is not ready.`, {
        description: error.message,
      });
      setBusy(false);
    }
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F0] grid lg:grid-cols-2">
      {/* Left / editorial panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-white/5 overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative">
          <Link to="/" data-testid="auth-back" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
        <div className="relative">
          <div className="overline mb-6">HustlrMate</div>
          <h2 className="font-serif-editorial font-light tracking-tighter leading-[1.02] text-4xl xl:text-5xl max-w-md">
            One year from now, you’ll wish{" "}
            <em className="italic text-[#F59E0B]">you started today.</em>
          </h2>
          <p className="mt-6 text-[#A1A1AA] font-light max-w-md leading-relaxed">
            Skip the Reddit threads. Skip the Discord ghosts. Get paired with someone who has already
            committed to showing up — and helping you do the same.
          </p>
        </div>
        <div className="relative flex items-center gap-6 text-xs text-[#A1A1AA] uppercase tracking-[0.2em]">
          <span>◆ Curated</span>
          <span>◆ Serious</span>
          <span>◆ Accountable</span>
        </div>
      </div>

      {/* Right / form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F5F5F0] mb-8">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>

          <div className="flex items-center gap-2 mb-8">
            <BrandLogo className="h-8 w-8" />
            <span className="font-serif-editorial text-2xl tracking-tight">HustlrMate</span>
          </div>

          <div className="overline mb-3">{mode === "signup" ? "Create your account" : "Welcome back"}</div>
          <h1 className="font-serif-editorial font-light tracking-tighter text-4xl sm:text-5xl leading-[1.05] mb-4">
            {mode === "signup" ? (
              <>Claim your seat at the <em className="italic text-[#F59E0B]">table.</em></>
            ) : (
              <>Sign in and <em className="italic text-[#F59E0B]">get to work.</em></>
            )}
          </h1>
          <p className="text-sm text-[#A1A1AA] font-light mb-8">
            {mode === "signup"
              ? "Tell us who you are. We’ll take it from there."
              : "Enter your details to continue where you left off."}
          </p>

          {/* Toggle */}
          <div className="inline-flex p-1 rounded-md border border-white/10 bg-[#101010] mb-6" role="tablist" data-testid="auth-mode-toggle">
            <button
              type="button"
              data-testid="auth-tab-signup"
              onClick={() => setMode("signup")}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.2em] rounded-sm transition-colors ${
                mode === "signup" ? "bg-[#F59E0B] text-black" : "text-[#A1A1AA] hover:text-[#F5F5F0]"
              }`}
            >
              Sign up
            </button>
            <button
              type="button"
              data-testid="auth-tab-signin"
              onClick={() => setMode("signin")}
              className={`px-4 py-1.5 text-xs uppercase tracking-[0.2em] rounded-sm transition-colors ${
                mode === "signin" ? "bg-[#F59E0B] text-black" : "text-[#A1A1AA] hover:text-[#F5F5F0]"
              }`}
            >
              Sign in
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <Field icon={User} label="Full name" type="text" placeholder="Ada Lovelace" testId="auth-name" value={form.name} onChange={setField("name")} />
            )}
            <Field icon={Mail} label="Email" type="email" placeholder="you@yourdomain.com" testId="auth-email" value={form.email} onChange={setField("email")} />
            <Field icon={Lock} label="Password" type="password" placeholder="••••••••" testId="auth-password" value={form.password} onChange={setField("password")} />

            <button
              type="submit"
              data-testid="auth-submit"
              disabled={busy}
              className="hm-btn-primary w-full inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-md text-sm font-semibold tracking-wide"
            >
              {busy ? "Working..." : mode === "signup" ? "Create account" : "Sign in"}
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>

            <div className="relative py-3">
              <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
              <div className="relative flex justify-center">
                <span className="bg-[#0A0A0A] px-3 text-[10px] uppercase tracking-[0.24em] text-[#A1A1AA]">or continue with</span>
              </div>
            </div>

            <button
              type="button"
              data-testid="auth-google"
              disabled={busy}
              onClick={() => onOAuth("google")}
              className="hm-btn-ghost w-full inline-flex justify-center items-center gap-2 px-6 py-3 rounded-md text-sm font-medium"
            >
              <Chrome className="w-4 h-4" strokeWidth={1.5} />
              Google
            </button>

            <button
              type="button"
              data-testid="auth-github"
              disabled={busy}
              onClick={() => onOAuth("github")}
              className="hm-btn-ghost w-full inline-flex justify-center items-center gap-2 px-6 py-3 rounded-md text-sm font-medium"
            >
              <Github className="w-4 h-4" strokeWidth={1.5} />
              GitHub
            </button>
          </form>

          {(STANDALONE_MODE || process.env.NODE_ENV !== "production") && (
            <section className="mt-6 p-4 border border-white/25 bg-white/[.025]" data-testid="winter-arc-preview-login">
              <div className="flex items-start gap-3 mb-3"><Snowflake className="w-5 h-5 mt-0.5 text-white"/><div><div className="text-[10px] uppercase tracking-[.24em] text-white">Winter Arc frontend preview</div><p className="text-xs text-white/45 mt-1">Bypasses backend auth and opens seeded seasonal UI data. Development and standalone builds only.</p></div></div>
              <button type="button" disabled={busy} onClick={onWinterArcPreview} className="w-full bg-white text-black inline-flex justify-center items-center gap-2 px-5 py-3 text-sm font-semibold hover:bg-neutral-200 disabled:opacity-50" data-testid="auth-winter-arc-preview">
                <Snowflake className="w-4 h-4" />{busy ? "Opening preview…" : "Preview as Winter Arc member"}
              </button>
            </section>
          )}

          {guestLoginEnabled && (
            <section className="mt-6 p-4 rounded-md border border-amber-500/30 bg-amber-500/[0.05]" data-testid="dev-guest-login">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="overline mb-1">Development mode</div>
                  <p className="text-xs text-[#A1A1AA]">Open the platform with a completed local guest profile.</p>
                </div>
                <span className="text-[9px] uppercase tracking-[0.18em] text-amber-400">ENV only</span>
              </div>
              <button type="button" disabled={busy} onClick={onGuestLogin} className="hm-btn-ghost w-full inline-flex justify-center items-center gap-2 px-5 py-3 rounded-md text-sm border-amber-500/30 hover:border-amber-500/60" data-testid="auth-skip-guest">
                <DoorOpen className="w-4 h-4" />
                {busy ? "Opening guest session…" : "Skip login and onboarding"}
              </button>
            </section>
          )}

          {devAccounts.length > 0 && (
            <section className="mt-8 pt-7 border-t border-white/10" data-testid="dev-account-picker">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <div className="overline mb-1">Development mode</div>
                  <h2 className="font-serif-editorial text-xl">Quick-login profiles</h2>
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm border border-amber-500/30 text-amber-400">Local only</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {devAccounts.map((account) => (
                  <button
                    key={account.key}
                    type="button"
                    disabled={busy}
                    onClick={() => onDevLogin(account)}
                    className="text-left p-3 rounded-md border border-white/10 bg-white/[0.02] hover:border-[#F59E0B]/40 hover:bg-[#F59E0B]/5 transition-colors disabled:opacity-50"
                    data-testid={`dev-login-${account.key}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm text-[#F5F5F0]">{account.fullName}</span>
                      <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm ${account.subscriptionTier === "pro" ? "bg-[#F59E0B] text-black" : "border border-white/15 text-[#A1A1AA]"}`}>{account.subscriptionTier}</span>
                    </div>
                    <p className="text-[11px] leading-relaxed text-[#A1A1AA]">{account.summary}</p>
                  </button>
                ))}
              </div>
            </section>
          )}

          <p className="mt-8 text-xs text-[#A1A1AA] font-light leading-relaxed">
            By continuing, you agree to our <a href="#" className="text-[#F5F5F0] underline decoration-white/20 underline-offset-4 hover:decoration-[#F59E0B]">Terms</a> and
            {" "}<a href="#" className="text-[#F5F5F0] underline decoration-white/20 underline-offset-4 hover:decoration-[#F59E0B]">Privacy Policy</a>. HustlrMate has a strict no-ghosting policy —
            we mean it.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type, placeholder, testId, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">{label}</span>
      <div className="relative">
        <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" strokeWidth={1.5} />
        <input
          data-testid={testId}
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          className="hm-input w-full pl-10 pr-4 py-3 rounded-md text-sm font-light placeholder:text-[#5b5b60]"
        />
      </div>
    </label>
  );
}
